'use client'

import { useId, useState } from 'react'
import { catLabel } from './blog-kit'

/**
 * Chips the post hero shows before "+N more": the category and five tags,
 * or the category and two on phones (blog.css hides the rest there), where a
 * long tag fills a line of its own.
 */
const VISIBLE = 6
const VISIBLE_PHONE = 3

/**
 * The tag chips under a post's headline. Posts carry 35 tags on median (up
 * to 50), which filled the first screen under the headline (Lloyd,
 * 2026-10-09); the hero now shows the category and the first five and the
 * rest expand in place. Every tag stays in the HTML (the extra chips are only
 * `hidden`), so nothing is lost to crawlers or to the reader.
 */
export default function HeroTags({
  tags,
  category,
}: {
  tags: string[]
  category: string
}) {
  const [open, setOpen] = useState(false)
  const wideId = useId()
  const extraId = useId()
  const ordered = tags.includes(category)
    ? [category, ...tags.filter(t => t !== category)]
    : tags
  const chip = (t: string, i: number) => (
    <span
      key={`${t}-${i}`}
      className={`chip${t === category ? ' is-cat' : ''}`}
    >
      {catLabel(t)}
    </span>
  )
  // Shown everywhere / shown on wide screens, hidden on phones until open /
  // hidden everywhere until open. The toggle controls the last two.
  const head = ordered.slice(0, VISIBLE_PHONE)
  const wide = ordered.slice(VISIBLE_PHONE, VISIBLE)
  const extra = ordered.slice(VISIBLE)
  const controls = [wide.length ? wideId : '', extra.length ? extraId : '']
    .filter(Boolean)
    .join(' ')
  return (
    <div className={`bhero__tags${open ? ' is-open' : ''}`}>
      {head.map(chip)}
      {wide.length > 0 && (
        <span id={wideId} className='bhero__wide'>
          {wide.map((t, i) => chip(t, i + VISIBLE_PHONE))}
        </span>
      )}
      {wide.length > 0 && (
        <>
          <span id={extraId} className='bhero__extra' hidden={!open}>
            {extra.map((t, i) => chip(t, i + VISIBLE))}
          </span>
          <button
            type='button'
            className={`chip bhero__toggle${
              extra.length === 0 ? ' bhero__toggle--phone' : ''
            }`}
            aria-expanded={open}
            aria-controls={controls}
            onClick={() => setOpen(o => !o)}
          >
            {open ? (
              'Show fewer'
            ) : (
              <>
                <span className='bhero__count--wide'>+{extra.length}</span>
                <span className='bhero__count--phone'>
                  +{ordered.length - VISIBLE_PHONE}
                </span>{' '}
                more
              </>
            )}
          </button>
        </>
      )}
    </div>
  )
}
